import { z } from "zod";

/**
 * Telemetrie-Logs pro Tenant. Aktuell nur ein Topic — Service-Cap-Events
 * für die adaptive Tuning des Match-Agent `serviceCapScore`.
 *
 * Liegt unter `.bidi-handel/tenants/<tenantId>/telemetry/service-cap-events.json`,
 * gitignored, append-on-match + update-on-patch.
 *
 * Größenkontrolle: pro Anfrage entstehen typisch 5-20 Cap-Events. Bei
 * 1000 Anfragen → ~10k Events. Geht ohne Probleme als JSON-Array.
 * Wenn das später wächst → Rotation auf JSONL + tail-N für Recommender.
 */

export const ServiceCapEventSchema = z.object({
  /** ISO-Timestamp wann der Cap angewendet wurde (= Pipeline-Phase-4). */
  createdAt: z.string(),
  /** Anfrage-ID damit wir später per-Anfrage analysieren können. */
  anfrageId: z.string(),
  /** Position-ID — eindeutig pro Anfrage. */
  positionId: z.string(),
  /** OZ aus dem LV (Redundanz für lesbare Debug-Ausgaben). */
  oz: z.string(),
  /** LLM-Score VOR der Cap-Anwendung. Wichtig für Score-Verteilung-Analyse. */
  topScoreBeforeCap: z.number().int().min(0).max(100),
  /** Cap-Wert der angewendet wurde (= TenantConfig.matchPolicy.serviceCapScore
   *  zum Zeitpunkt der Match-Phase). */
  capApplied: z.number().int().min(0).max(100),
  /** Erkannte Service-Verben (für Debug + UI-Anzeige). */
  detectedVerbs: z.array(z.string()).default([]),
  /** User-Entscheidung — initial „pending", wird via PATCH aktualisiert.
   *  - picked: User hat trotz Cap einen Artikel übernommen → Cap zu streng
   *  - excluded: User hat Position als „Nicht angeboten" markiert
   *  - removed: User hatte gepickt, dann wieder entfernt
   *  - schnellkalkulation: User hat Komponenten-Liste statt Material-Pick
   *    angelegt → Cap war richtig (Service-Anteil war real, Material-Match
   *    nicht ausreichend)
   *  - pending: noch keine User-Aktion */
  userDecision: z
    .enum(["pending", "picked", "excluded", "removed", "schnellkalkulation"])
    .default("pending"),
  /** Wann die User-Entscheidung fiel. */
  decidedAt: z.string().optional(),
  /** Bei „picked": welcher Artikel-Code wurde übernommen. */
  pickedArtikelCode: z.string().optional(),
});
export type ServiceCapEvent = z.infer<typeof ServiceCapEventSchema>;

export const ServiceCapTelemetrySchema = z.object({
  schemaVersion: z.literal(1).default(1),
  events: z.array(ServiceCapEventSchema).default([]),
});
export type ServiceCapTelemetry = z.infer<typeof ServiceCapTelemetrySchema>;

/**
 * Recommender-Output für die Match-Policy.
 *
 * `recommendedCap` ist nur gesetzt wenn genug Daten da sind UND die
 * Empfehlung sich vom aktuellen Cap unterscheidet. Sonst rationale-only.
 */
export const ServiceCapRecommendationSchema = z.object({
  sampleSize: z.number().int().nonnegative(),
  decidedCount: z.number().int().nonnegative(),
  pickedCount: z.number().int().nonnegative(),
  excludedCount: z.number().int().nonnegative(),
  removedCount: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  pickedRatio: z.number().min(0).max(1),
  currentCap: z.number().int().min(0).max(100),
  recommendedCap: z.number().int().min(0).max(100).nullable(),
  rationale: z.string(),
});
export type ServiceCapRecommendation = z.infer<
  typeof ServiceCapRecommendationSchema
>;
